import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowCircleLeft, FaArrowAltCircleRight, FaFileInvoice } from 'react-icons/fa';
import PageHeader from '../../shared/PageHeader';
import ListDetailsCard from '../../shared/ListDetailsCard';
import ItemsListCollapseableTable from '../../shared/ItemsListCollapseable';
import { Panel } from 'primereact/panel';

const ClaimDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const claimRow = state?.claimDetails;
  const auditIssues = useMemo(() => state?.auditIssues || [], [state?.auditIssues]);

  const patient = claimRow?.patient_info || {};
  const practitioner = claimRow?.practitioner_info || {};
  const claim = claimRow?.claim || {};
  const rcmRequest = claim?.rcm_request || {};
  const insurancePlan = patient?.insurance_plans?.[0] || {};
  const claimItems = useMemo(
    () =>
      (rcmRequest.items || []).map((item, index) => ({
        ...item,
        __auditItemIndex: index,
        __auditRowKey: `claim-item-${index}`,
      })),
    [rcmRequest.items]
  );
  const groupedAuditIssues = useMemo(() => {
    const byItemKey = {};
    const claimLevel = [];

    auditIssues.forEach(issue => {
      let itemIndex = Number(issue?.item_index);
      if (!Number.isInteger(itemIndex) || itemIndex < 0 || itemIndex >= claimItems.length) {
        itemIndex = issue?.item_id
          ? claimItems.findIndex(item => item?.item_id === issue.item_id)
          : -1;
      }
      if (itemIndex < 0) {
        claimLevel.push(issue);
        return;
      }
      const key = claimItems[itemIndex].__auditRowKey;
      byItemKey[key] = [...(byItemKey[key] || []), issue];
    });

    return { byItemKey, claimLevel };
  }, [auditIssues, claimItems]);
  const [expandedItemRows, setExpandedItemRows] = useState({});

  useEffect(() => {
    const affectedRows = {};
    Object.keys(groupedAuditIssues.byItemKey).forEach(key => {
      affectedRows[key] = true;
    });
    setExpandedItemRows(affectedRows);
  }, [groupedAuditIssues]);

  const formatAuditLabel = value =>
    String(value || '')
      .split(/[_-]+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

  const formatAuditValue = value => {
    if (value === null || value === undefined || value === '') return '--';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const diagnosisColumns = [
    {
      field: 'seq_no',
      header: t('Sequence'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'type',
      header: t('Type'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'code',
      header: t('Code'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'description',
      header: t('Description'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'on_admission',
      header: t('On Admission'),
      headerCellClass: '!bg-gray-200',
      body: (rowData) => rowData?.on_admission ? t('Yes') : t('No'),
    },
  ];

  // Items table columns
  const itemsColumns = [
    {
      field: 'seq_no',
      header: t('Seq'),
      headerCellClass: '!bg-gray-200',
    },
     {
      field: 'type',
      header: t('Type'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'non_standard_code',
      header: t('Non Standard Code'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'standard_code',
      header: t('Standard Code'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'standard_description',
      header: t('Standard Description'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'quantity',
      header: t('Quantity'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'unit_price',
      header: t('Unit Price'),
      headerCellClass: '!bg-gray-200',
    },
    {
      field: 'net',
      header: t('Net Amount'),
      headerCellClass: '!bg-gray-200',
    },
  ];

  // Row expansion template for items
  const rowExpansionItemsTemplate = (data, t) => {
    const itemAuditIssues = groupedAuditIssues.byItemKey[data?.__auditRowKey] || [];
    const rowData = [
      {
        label: t('Non-Standard Code'),
        text: data?.non_standard_code || '--',
      },
      {
        label: t('Non-Standard Description'),
        text: data?.non_standard_description || '--',
      },
      {
        label: t('Body Site'),
        text: data?.body_site || '--',
      },
      {
        label: t('Sub Site'),
        text: data?.sub_site || '--',
      },
      {
        label: t('Service Start Date'),
        text: data?.service_start_date || '--',
      },
      {
        label: t('Service End Date'),
        text: data?.service_end_date || '--',
      },
      {
        label: t('Total Quantity'),
        text: data?.total_quantity || '--',
      },
      {
        label: t('Discount Amount'),
        text: data?.discount_amount || '--',
      },
      {
        label: t('Tax Amount'),
        text: data?.tax_amount || '--',
      },
      {
        label: t('Factor'),
        text: data?.factor ?? '--',
      },
      {
        label: t('Payer Share'),
        text: data?.payer_share || '--',
      },
      {
        label: t('Invoice Number'),
        text: data?.invoice_no || '--',
      },
    ];

    return (
      <Panel
        pt={{
          root: { className: 'rounded' },
          toggleablecontent: { className: 'rounded' },
        }}
      >
        <div className='grid grid-cols-3 lg:grid-cols-5 md:grid-cols-4'>
          {rowData?.map((item, index) => (
            <div className='mb-2' key={index}>
              <h5 className='text-12-gray mb-1'>{item?.label}</h5>
              <h4 className='text-14-gray !font-semibold'>{item?.text}</h4>
            </div>
          ))}
        </div>
        {itemAuditIssues.length > 0 && (
          <div className='mt-3 border-t border-yellow-300 pt-3'>
            <h5 className='mb-2 text-sm font-semibold text-amber-900'>
              {t('Audit Issues')} ({itemAuditIssues.length})
            </h5>
            <div className='space-y-2'>
              {itemAuditIssues.map(issue => (
                <div
                  key={issue?.id || `${issue?.issue_type}-${issue?.item_index}`}
                  className='rounded-md border border-yellow-300 bg-yellow-50 p-3'
                >
                  <div className='mb-2 font-semibold text-amber-900'>
                    {formatAuditLabel(issue?.issue_type) || '--'}
                  </div>
                  <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
                    <div>
                      <div className='text-xs text-gray-500'>{t('Actual Value')}</div>
                      <div className='font-medium text-gray-800'>{formatAuditValue(issue?.actual_value)}</div>
                    </div>
                    <div>
                      <div className='text-xs text-gray-500'>{t('Expected Value')}</div>
                      <div className='font-medium text-gray-800'>{formatAuditValue(issue?.expected_value)}</div>
                    </div>
                    <div>
                      <div className='text-xs text-gray-500'>{t('Corrected Value')}</div>
                      <div className='font-medium text-gray-800'>{formatAuditValue(issue?.corrected_value)}</div>
                    </div>
                    <div>
                      <div className='text-xs text-gray-500'>{t('Status')}</div>
                      <div className='font-medium text-gray-800'>{formatAuditLabel(issue?.status) || '--'}</div>
                    </div>
                  </div>
                  {!!issue?.failure_reason && (
                    <div className='mt-2 text-sm text-red-700'>
                      <span className='font-semibold'>{t('Failure Reason')}:</span> {issue.failure_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </Panel>
    );
  };

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <PageHeader
        title={t('Claim Details')}
        iconSize={20}
        Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowAltCircleRight}
        onIconClick={() => navigate(-1)}
        RightIcon={FaFileInvoice}
      />
      {!claimRow ? (
        <p className='text-14-gray mt-4'>{t('No data provided.')}</p>
      ) : (
        <>
          <div className='mt-2'>
            <ListDetailsCard
              listData={[
                { label: t('Patient Name'), text: patient.full_name || '--' },
                { label: t('MRN'), text: patient.file_no || '--' },
                { label: t('Document ID'), text: patient.document_id || '--' },
                { label: t('Contact Number'), text: patient.contact || '--' },
                { label: t('Gender'), text: patient.gender || '--' },
                { label: t('Nationality'), text: patient.nationality || '--' },
                { label: t('Claim ID'), text: claim.claim_id || '--' },
                { label: t('Visit ID'), text: claim.visit_id || '--' },
                { label: t('EMR ID'), text: claim.emr_id || '--' },
                { label: t('Episode Number'), text: rcmRequest.episode_no || '--' },
                { label: t('Claim Type'), text: claim.claim_type || '--' },
                { label: t('Status'), text: claim.status || '--' },
                { label: t('Priority'), text: rcmRequest.priority || '--' },
                { label: t('Date Ordered'), text: rcmRequest.date_ordered || '--' },
                { label: t('Practitioner Name'), text: practitioner.name || '--' },
                { label: t('Department'), text: practitioner.department?.name_en || practitioner.department?.name_ar || '--' },
                { label: t('License'), text: practitioner.license || '--' },
                ...(insurancePlan && Object.keys(insurancePlan).length > 0 ? [
                  { label: t('Payer Name'), text: insurancePlan.payer_name || '--' },
                  { label: t('Policy Number'), text: insurancePlan.policy_number || '--' },
                  { label: t('Coverage Type'), text: insurancePlan.coverage_type || '--' },
                ] : []),
              ]}
            />
          </div>

          {rcmRequest.diagnosis && rcmRequest.diagnosis.length > 0 && (
            <div className='mt-2'>
              <h5 className='text-16-dark !font-semibold mb-2'>{t('Diagnosis')}</h5>
              <ItemsListCollapseableTable
                data={rcmRequest.diagnosis}
                columns={diagnosisColumns}
                dataKey='code'
                emptyMessage={t('No Diagnosis Found')}
              />
            </div>
          )}

          {rcmRequest.items && rcmRequest.items.some(item => item.supporting_info) && (
            <div className='mt-2'>
              <h5 className='text-16-dark !font-semibold mb-2'>{t('Supporting Information')}</h5>
              <ItemsListCollapseableTable
                data={rcmRequest.items.filter(item => item.supporting_info)}
                columns={[
                  {
                    field: 'item_name',
                    header: t('Item Name'),
                    headerCellClass: '!bg-gray-200',
                    body: rowData =>
                      rowData?.item_name ||
                      rowData?.standard_description ||
                      rowData?.non_standard_description ||
                      '--',
                  },
                  {
                    field: 'supporting_info',
                    header: t('Supporting Info'),
                    headerCellClass: '!bg-gray-200',
                  },
                  {
                    field: 'service_start_date',
                    header: t('Service Date'),
                    headerCellClass: '!bg-gray-200',
                  },
                ]}
                dataKey='seq_no'
                emptyMessage={t('No Supporting Information Found')}
              />
            </div>
          )}
          {groupedAuditIssues.claimLevel.length > 0 && (
            <div className='mt-2 rounded-md border border-yellow-300 bg-yellow-50 p-3'>
              <h5 className='mb-2 text-sm font-semibold text-amber-900'>{t('Claim Audit Issues')}</h5>
              <div className='space-y-1'>
                {groupedAuditIssues.claimLevel.map(issue => (
                  <div key={issue?.id || issue?.issue_type} className='text-sm text-amber-900'>
                    <span className='font-semibold'>{formatAuditLabel(issue?.issue_type)}:</span>{' '}
                    {t('Actual Value')} {formatAuditValue(issue?.actual_value)} · {t('Expected Value')}{' '}
                    {formatAuditValue(issue?.expected_value)} · {t('Status')} {formatAuditLabel(issue?.status)}
                  </div>
                ))}
              </div>
            </div>
          )}
          {claimItems.length > 0 && (
            <div className='mt-2'>
              <h5 className='text-16-dark !font-semibold mb-2'>{t('Items')}</h5>
              <ItemsListCollapseableTable
                data={claimItems}
                columns={itemsColumns}
                rowExpansionTemplate={rowExpansionItemsTemplate}
                dataKey='__auditRowKey'
                expandedRows={expandedItemRows}
                onRowToggle={event => setExpandedItemRows(event.data)}
                rowClassName={row =>
                  groupedAuditIssues.byItemKey[row?.__auditRowKey]?.length
                    ? '!bg-yellow-100'
                    : ''
                }
                expandedRowClassName='!bg-yellow-100'
                expansionBodyClassName='!bg-yellow-50'
                emptyMessage={t('No Items Found')}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClaimDetails;
